import React, { useMemo, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Box,
  Grid,
  Stack,
  Button,
  TextField,
  MenuItem,
  Typography,
  IconButton,
  Menu,
  Snackbar,
  Alert,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  PageHeader,
  LoadingState,
  StatusChip,
  DetailCard,
  InfoRow,
  ConfirmDialog,
  InvoiceImportPageImage,
} from '../components';
import {
  usePurchaseInvoice,
  useReceiveGoods,
  useDeletePurchaseInvoice,
  useUpdatePaymentStatus,
} from '../hooks/usePurchaseInvoices';
import { useInvoiceImport } from '../hooks/useInvoiceImports';
import { useWarehouses } from '../hooks/useWarehouses';
import { fetchOriginalFileObjectUrl } from '../api/invoice-imports';
import { useBusinessUuid } from '../useBusinessUuid';
import {
  colors,
  formatCurrency,
  formatDate,
  SHOW_WAREHOUSE_FIELDS,
} from '../constants';

const PAYMENT_OPTIONS = [
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'partial', label: 'Partially Paid' },
  { value: 'paid', label: 'Paid' },
];

export default function PurchaseInvoiceDetail() {
  const { uuid } = useParams();
  const history = useHistory();
  const businessUuid = useBusinessUuid();

  const { data: invoice, isLoading } = usePurchaseInvoice(uuid);
  const receiveGoods = useReceiveGoods();
  const deleteInvoice = useDeletePurchaseInvoice();
  const updatePaymentStatus = useUpdatePaymentStatus();
  const { data: warehouses = [] } = useWarehouses();
  const { data: sourceImport } = useInvoiceImport(invoice?.sourceImportUuid ?? '');

  const [receivingMode, setReceivingMode] = useState(false);
  const [receivedQtys, setReceivedQtys] = useState({});
  const [receiveWarehouseUuid, setReceiveWarehouseUuid] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [paymentAnchor, setPaymentAnchor] = useState(null);
  const [toast, setToast] = useState(null);

  const warehouseOptions = useMemo(
    () => warehouses.filter((w) => w.isActive).map((w) => ({ value: w.uuid, label: w.name })),
    [warehouses]
  );
  const warehouseName = useMemo(() => {
    if (!invoice?.warehouseUuid) return null;
    return warehouses.find((w) => w.uuid === invoice.warehouseUuid)?.name ?? null;
  }, [invoice, warehouses]);

  if (isLoading) return <LoadingState />;
  if (!invoice) {
    return (
      <>
        <PageHeader title="Not Found" backTo="/inventory-management/purchase-invoices" />
        <Typography sx={{ color: colors.gray500 }}>Item not found</Typography>
      </>
    );
  }

  const canReceive = invoice.status !== 'received' && invoice.status !== 'cancelled';
  const canDelete = invoice.status === 'draft';

  const startReceiving = () => {
    setReceiveWarehouseUuid(invoice.warehouseUuid || '');
    const prefill = {};
    (invoice.items || []).forEach((it) => {
      if (it.remainingQuantity > 0) prefill[it.uuid] = String(it.remainingQuantity);
    });
    setReceivedQtys(prefill);
    setReceivingMode(true);
  };

  const handleReceive = async () => {
    const receiveItems = Object.entries(receivedQtys)
      .filter(([, qty]) => parseFloat(qty) > 0)
      .map(([itemUuid, qty]) => ({ itemUuid, receivedQuantity: parseFloat(qty) }));
    if (receiveItems.length === 0) {
      setToast({ severity: 'error', message: 'Enter at least one quantity to receive.' });
      return;
    }
    try {
      await receiveGoods.mutateAsync({
        uuid: invoice.uuid,
        data: {
          receivedBy: 'partner',
          items: receiveItems,
          warehouseUuid: receiveWarehouseUuid || undefined,
        },
      });
      setReceivingMode(false);
      setReceivedQtys({});
      setReceiveWarehouseUuid('');
      setToast({ severity: 'success', message: 'Goods received' });
    } catch (e) {
      setToast({ severity: 'error', message: e?.response?.data?.message || 'Receive failed' });
    }
  };

  const handlePaymentStatus = async (newStatus) => {
    setPaymentAnchor(null);
    try {
      await updatePaymentStatus.mutateAsync({ uuid: invoice.uuid, paymentStatus: newStatus, businessUuid });
    } catch (e) {
      setToast({ severity: 'error', message: e?.response?.data?.message || 'Update failed' });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteInvoice.mutateAsync({ uuid: invoice.uuid, businessUuid });
      setDeleteOpen(false);
      history.push('/inventory-management/purchase-invoices');
    } catch (e) {
      setDeleteOpen(false);
      setToast({ severity: 'error', message: e?.response?.data?.message || 'Delete failed' });
    }
  };

  const downloadOriginal = async () => {
    try {
      const { url } = await fetchOriginalFileObjectUrl(sourceImport.uuid, true);
      const a = document.createElement('a');
      a.href = url;
      a.download = sourceImport.file?.originalName || 'invoice';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      setToast({ severity: 'error', message: 'Could not download document.' });
    }
  };

  const openOriginal = async () => {
    try {
      const { url } = await fetchOriginalFileObjectUrl(sourceImport.uuid);
      window.open(url, '_blank');
    } catch {
      setToast({ severity: 'error', message: 'Could not open document.' });
    }
  };

  return (
    <>
      <PageHeader
        title={invoice.invoiceNumber}
        subtitle={invoice.vendorName}
        backTo="/inventory-management/purchase-invoices"
        actions={
          <>
            <StatusChip status={invoice.status} />
            {invoice.status !== 'cancelled' ? (
              <Button
                size="small"
                endIcon={<ArrowDropDownIcon />}
                onClick={(e) => setPaymentAnchor(e.currentTarget)}
                sx={{ textTransform: 'none', p: 0 }}
              >
                <StatusChip status={invoice.paymentStatus} />
              </Button>
            ) : (
              <StatusChip status={invoice.paymentStatus} />
            )}
          </>
        }
      />

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={5}>
          <DetailCard title="Summary">
            {invoice.externalInvoiceNumber && <InfoRow label="Vendor Invoice" value={invoice.externalInvoiceNumber} />}
            <InfoRow label="Invoice Date" value={formatDate(invoice.invoiceDate)} />
            {SHOW_WAREHOUSE_FIELDS && <InfoRow label="Warehouse" value={warehouseName} />}
            <InfoRow label="Subtotal" value={formatCurrency(invoice.subtotal)} />
            <InfoRow label="Tax" value={formatCurrency(invoice.taxAmount)} />
            <InfoRow label="Shipping" value={formatCurrency(invoice.shippingCost)} />
            <InfoRow label="Total" value={<b>{formatCurrency(invoice.totalAmount)}</b>} />
            {invoice.notes && <InfoRow label="Notes" value={invoice.notes} />}
          </DetailCard>

          {sourceImport && sourceImport.file?.pageCount > 0 && (
            <Box sx={{ mt: 2.5 }}>
              <DetailCard
                title="Original Document"
                action={
                  <Button size="small" startIcon={<DownloadOutlinedIcon />} onClick={downloadOriginal} sx={{ textTransform: 'none' }}>
                    Download
                  </Button>
                }
              >
                <Typography variant="caption" sx={{ color: colors.gray500, display: 'block', mb: 1 }}>
                  {sourceImport.file.originalName}
                </Typography>
                <Stack direction="row" spacing={1.5} sx={{ overflowX: 'auto', pb: 1 }}>
                  {Array.from({ length: sourceImport.file.pageCount }).map((_, idx) => (
                    <InvoiceImportPageImage key={idx} uuid={sourceImport.uuid} index={idx} onClick={openOriginal} />
                  ))}
                </Stack>
              </DetailCard>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} md={7}>
          <DetailCard
            title="Line Items"
            action={
              canReceive && !receivingMode ? (
                <Button size="small" variant="contained" startIcon={<CheckCircleOutlineIcon />} onClick={startReceiving} sx={{ backgroundColor: colors.primary, textTransform: 'none' }}>
                  Receive Goods
                </Button>
              ) : null
            }
          >
            {receivingMode && SHOW_WAREHOUSE_FIELDS && (
              <TextField
                select
                label="Receive into Warehouse"
                value={receiveWarehouseUuid}
                onChange={(e) => setReceiveWarehouseUuid(e.target.value)}
                fullWidth
                size="small"
                sx={{ mb: 2 }}
              >
                <MenuItem value="">Select warehouse (optional)</MenuItem>
                {warehouseOptions.map((w) => (
                  <MenuItem key={w.value} value={w.value}>{w.label}</MenuItem>
                ))}
              </TextField>
            )}

            {invoice.items?.map((item) => (
              <Box key={item.uuid} sx={{ py: 1.25, borderBottom: `1px solid ${colors.gray100}` }}>
                <Stack direction="row" justifyContent="space-between">
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.name}</Typography>
                    <Typography variant="caption" sx={{ color: colors.gray500 }}>SKU: {item.sku}</Typography>
                    {item.wasAutoCreated && (
                      <Box sx={{ mt: 0.5 }}>
                        <StatusChip status="info" label="Auto-created" />
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" sx={{ color: colors.gray700 }}>
                      {formatCurrency(item.unitCost)} x {item.orderedQuantity}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {formatCurrency(item.totalCost)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.gray400 }}>
                      Received: {item.receivedQuantity} / {item.orderedQuantity}
                    </Typography>
                  </Box>
                </Stack>
                {receivingMode && item.remainingQuantity > 0 && (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ color: colors.gray500 }}>Receive:</Typography>
                    <TextField
                      size="small"
                      type="number"
                      value={receivedQtys[item.uuid] || ''}
                      onChange={(e) => setReceivedQtys((prev) => ({ ...prev, [item.uuid]: e.target.value }))}
                      placeholder={`max ${item.remainingQuantity}`}
                      sx={{ width: 120 }}
                    />
                  </Stack>
                )}
              </Box>
            ))}

            {receivingMode && (
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setReceivingMode(false);
                    setReceivedQtys({});
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleReceive}
                  disabled={receiveGoods.isPending}
                  sx={{ backgroundColor: colors.primary, textTransform: 'none' }}
                >
                  Confirm Receipt
                </Button>
              </Stack>
            )}
          </DetailCard>

          {canDelete && (
            <Box sx={{ mt: 2.5 }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => setDeleteOpen(true)}
                sx={{ textTransform: 'none' }}
              >
                Delete Draft Invoice
              </Button>
            </Box>
          )}
        </Grid>
      </Grid>

      <Menu anchorEl={paymentAnchor} open={!!paymentAnchor} onClose={() => setPaymentAnchor(null)}>
        {PAYMENT_OPTIONS.map((opt) => (
          <MenuItem
            key={opt.value}
            selected={invoice.paymentStatus === opt.value}
            onClick={() => handlePaymentStatus(opt.value)}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Menu>

      <ConfirmDialog
        open={deleteOpen}
        title="Delete Invoice"
        message={`Delete invoice "${invoice.invoiceNumber}"? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={deleteInvoice.isPending}
        onConfirm={handleDelete}
        onClose={() => setDeleteOpen(false)}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}
